import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/Layout';
import StickyActionHeader from '../../components/StickyActionHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Camera, 
  Upload,
  ImageSquare,
  User,
  Images,
  Download,
  X,
  ArrowLeft,
  ArrowRight,
  MagnifyingGlassPlus,
  UserCircle,
  SpinnerGap,
  CheckCircle,
  Trash,
  VideoCamera,
  WhatsappLogo
} from '@phosphor-icons/react';
import api from '../../services/api';
import { toast } from 'sonner';
import { resolveMediaUrl } from '../../utils/media';
import useEventTypeConfig from '../../hooks/useEventTypeConfig';
import EventScheduledReminderCard from '../../components/EventScheduledReminderCard';
import GuestExperienceMediaManager from '../../components/GuestExperienceMediaManager';
import AIPremiumButton from '../../components/AIPremiumGate';
import InfoTip from '../../components/InfoTip';

// PhotoGrid component moved outside to avoid recreation on each render
function PhotoGrid({ photoList, emptyMessage, loading, onOpenLightbox, onDownload }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
      {photoList.map((photo, index) => (
        <div 
          key={photo.id || index} 
          className="aspect-square rounded-lg overflow-hidden bg-wedding-sage/10 cursor-pointer hover:opacity-90 transition-opacity relative group"
          data-testid={`photo-${photo.id || index}`}
          onClick={() => onOpenLightbox(photoList, index)}
        >
          <img 
            src={resolveMediaUrl(photo.url, { preferProxy: true })} 
            alt={photo.caption || 'Event photo'}
            className="w-full h-full object-cover"
          />
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button
              size="icon"
              variant="secondary"
              className="h-8 w-8"
              onClick={(e) => { e.stopPropagation(); onOpenLightbox(photoList, index); }}
            >
              <MagnifyingGlassPlus className="w-4 h-4" />
            </Button>
            <Button
              size="icon"
              variant="secondary"
              className="h-8 w-8"
              onClick={(e) => { e.stopPropagation(); onDownload(photo); }}
            >
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
      {photoList.length === 0 && !loading && (
        <div className="col-span-full py-12 text-center">
          <ImageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{emptyMessage}</p>
        </div>
      )}
    </div>
  );
}

export default function PhotoManagement() {
  const { currentEvent, user, hasAdminAccess, switchEvent } = useAuth();
  const eventTypeConfig = useEventTypeConfig(currentEvent);
  const canManageGuestExperience = ['event_manager', 'bride', 'groom', 'host'].includes(user?.role)
    && ['manager', 'bride', 'groom', 'host', undefined, null].includes(currentEvent?.viewer_role);
  const [photos, setPhotos] = useState([]);
  const [myPhotos, setMyPhotos] = useState([]);
  const [weddingAlbumPhotos, setWeddingAlbumPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [shareWithFeatured, setShareWithFeatured] = useState(true);
  const [activeTab, setActiveTab] = useState('my');
  const [workspaceTab, setWorkspaceTab] = useState(() => {
    const savedTab = window.sessionStorage.getItem('photo-management-workspace-tab');
    return ['hero', 'album', 'photo-wall'].includes(savedTab) ? savedTab : 'hero';
  });
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [currentPhotoList, setCurrentPhotoList] = useState([]);
  
  // Face registration state
  const [faceDialogOpen, setFaceDialogOpen] = useState(false);
  const [hasFaceRegistered, setHasFaceRegistered] = useState(false);
  const [facePhoto, setFacePhoto] = useState(null);
  const [faceLoading, setFaceLoading] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [scanningPhotos, setScanningPhotos] = useState(false);
  const [scanStatus, setScanStatus] = useState({
    last_scanned_at: null,
    total_scannable_photos: 0,
    new_photo_count: 0,
    has_new_photos: false
  });
  const [cameraMode, setCameraMode] = useState(false);
  const [galleryLinkInput, setGalleryLinkInput] = useState('');
  const [savingGalleryLink, setSavingGalleryLink] = useState(false);
  const [showPhotoWhatsAppPrompt, setShowPhotoWhatsAppPrompt] = useState(false);
  const [sendingPhotoMessage, setSendingPhotoMessage] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);

  const fetchPhotos = useCallback(async () => {
    if (!currentEvent?.id) return;
    try {
      const [allRes, myRes, weddingRes] = await Promise.all([
        api.get(`/events/${currentEvent.id}/photos`),
        api.get(`/events/${currentEvent.id}/photos/my-photos`),
        api.get(`/events/${currentEvent.id}/photos/wedding-album`)
      ]);
      setPhotos(allRes.data);
      setMyPhotos(myRes.data);
      setWeddingAlbumPhotos(weddingRes.data);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    } finally {
      setLoading(false);
    }
  }, [currentEvent?.id]);

  const loadFaceModels = useCallback(async () => {
    try {
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri('/models'),
        faceapi.nets.faceLandmark68Net.loadFromUri('/models'),
        faceapi.nets.faceRecognitionNet.loadFromUri('/models')
      ]);
      setModelsLoaded(true);
      console.log('Face-api models loaded');
    } catch (error) {
      console.error('Failed to load face models:', error);
    }
  }, []);

  const checkFaceStatus = useCallback(async () => {
    try {
      const response = await api.get('/users/face-status');
      setHasFaceRegistered(response.data.has_face_registered);
      setFacePhoto(response.data.profile_photo);
    } catch (error) {
      console.error('Failed to check face status:', error);
    }
  }, []);

  const fetchScanStatus = useCallback(async () => {
    if (!currentEvent?.id) return;
    try {
      const response = await api.get(`/events/${currentEvent.id}/photos/scan-status`);
      setScanStatus(response.data || {
        last_scanned_at: null,
        total_scannable_photos: 0,
        new_photo_count: 0,
        has_new_photos: false
      });
    } catch (error) {
      console.error('Failed to fetch photo scan status:', error);
    }
  }, [currentEvent?.id]);

  useEffect(() => {
    loadFaceModels();
    fetchPhotos();
    checkFaceStatus();
    fetchScanStatus();
  }, [loadFaceModels, fetchPhotos, checkFaceStatus, fetchScanStatus]);

  useEffect(() => {
    return () => {
      // Cleanup camera stream on unmount
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    setGalleryLinkInput(currentEvent?.wedding_photos_link || '');
  }, [currentEvent?.wedding_photos_link]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('album_type', 'public');
        formData.append('share_with_featured', shareWithFeatured);
        
        await api.post(`/events/${currentEvent.id}/photos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(`${files.length} photo(s) uploaded successfully`);
      fetchPhotos();
      fetchScanStatus();
      if (hasAdminAccess) {
        setShowPhotoWhatsAppPrompt(true);
      }
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const openLightbox = useCallback((photoList, index) => {
    setCurrentPhotoList(photoList);
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % currentPhotoList.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + currentPhotoList.length) % currentPhotoList.length);
  };

  const handleDownload = useCallback(async (photo) => {
    try {
      const imageUrl = resolveMediaUrl(photo.url, { preferProxy: true });
      
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `photo_${photo.id || 'download'}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Photo downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download photo');
    }
  }, []);

  // Face Registration Functions
  const startCamera = async () => {
    setCameraMode(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Failed to access camera');
      setCameraMode(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraMode(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    
    canvas.toBlob((blob) => {
      const file = new File([blob], 'face_capture.jpg', { type: 'image/jpeg' });
      processAndUploadFace(file);
    }, 'image/jpeg', 0.9);
    
    stopCamera();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processAndUploadFace(file);
    }
  };

  const scanPhotosForFaces = useCallback(async () => {
    if (!modelsLoaded || !photos.length || !currentEvent?.id) return;

    const scannablePhotos = photos.filter((photo) => {
      if (!photo?.share_with_featured) return false;
      if (!scanStatus?.last_scanned_at) return true;
      if (!photo?.created_at) return false;
      return new Date(photo.created_at) > new Date(scanStatus.last_scanned_at);
    });

    if (!scannablePhotos.length) {
      toast.info('No new photos to scan');
      return;
    }
    
    setScanningPhotos(true);
    toast.info('Scanning new photos for your face... This may take a moment.');
    
    try {
      const photoDescriptors = {};
      
      for (const photo of scannablePhotos) {
        try {
          const imageUrl = resolveMediaUrl(photo.url, { preferProxy: true });
          
          const img = await faceapi.fetchImage(imageUrl);
          const detections = await faceapi
            .detectAllFaces(img, new faceapi.TinyFaceDetectorOptions())
            .withFaceLandmarks()
            .withFaceDescriptors();
          
          photoDescriptors[photo.id] = detections.map(d => Array.from(d.descriptor));
        } catch (err) {
          console.warn(`Failed to process photo ${photo.id}:`, err);
        }
      }
      
      if (Object.keys(photoDescriptors).length > 0) {
        const formData = new FormData();
        formData.append('face_descriptors', JSON.stringify(photoDescriptors));
        
        const response = await api.post(
          `/events/${currentEvent.id}/photos/scan-faces`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        toast.success(response.data.message);
        fetchPhotos(); // Refresh to update my photos
        fetchScanStatus();
      } else {
        toast.info('No faces detected in any photos');
      }
      
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Failed to scan photos');
    } finally {
      setScanningPhotos(false);
    }
  }, [modelsLoaded, photos, scanStatus?.last_scanned_at, currentEvent?.id, fetchPhotos, fetchScanStatus]);

  const processAndUploadFace = async (file) => {
    if (!modelsLoaded) {
      toast.error('Face recognition models are still loading. Please wait.');
      return;
    }

    setFaceLoading(true);
    try {
      // Create image element for face-api
      const img = await faceapi.bufferToImage(file);
      
      // Detect face and get descriptor
      const detection = await faceapi
        .detectSingleFace(img, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptor();
      
      if (!detection) {
        toast.error('No face detected in the image. Please try again with a clear photo of your face.');
        setFaceLoading(false);
        return;
      }
      
      // Upload to server
      const formData = new FormData();
      formData.append('file', file);
      formData.append('face_descriptor', JSON.stringify(Array.from(detection.descriptor)));
      
      await api.post('/users/face-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Face registered successfully! Now scanning photos for matches...');
      setHasFaceRegistered(true);
      setFaceDialogOpen(false);
      
      // Automatically scan photos for this user
      await scanPhotosForFaces();
      
    } catch (error) {
      console.error('Face registration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to register face');
    } finally {
      setFaceLoading(false);
    }
  };

  const removeFaceRegistration = async () => {
    try {
      await api.delete('/users/face-photo');
      setHasFaceRegistered(false);
      setFacePhoto(null);
      toast.success('Face registration removed');
      fetchPhotos(); // Refresh to update my photos
      fetchScanStatus();
    } catch (error) {
      toast.error('Failed to remove face registration');
    }
  };

  const saveGalleryLink = async () => {
    if (!currentEvent?.id) return;

    const trimmed = galleryLinkInput.trim();
    if (trimmed) {
      try {
        // Validate URL before sending
        new URL(trimmed);
      } catch {
        toast.error('Please enter a valid gallery URL');
        return;
      }
    }

    setSavingGalleryLink(true);
    try {
      const response = await api.put(`/events/${currentEvent.id}/wedding-photos`, {
        album_link: trimmed || null
      });
      await switchEvent(currentEvent.id);
      toast.success('Gallery link updated');
      if (response.data?.should_prompt_whatsapp) {
        setShowPhotoWhatsAppPrompt(true);
      }
    } catch (error) {
      toast.error('Failed to update gallery link');
    } finally {
      setSavingGalleryLink(false);
    }
  };

  const sendPhotoUpdateMessage = async () => {
    if (!currentEvent?.id) return;

    const formData = new FormData();
    formData.append('template_type', 'photos');

    try {
      setSendingPhotoMessage(true);
      const response = await api.post(
        `/events/${currentEvent.id}/whatsapp/send-template`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const summary = response.data?.summary || {};
      toast.success(`Photo update sent to ${summary.sent || 0} guest(s)`);
      if (summary.failed || summary.skipped) {
        toast.info(`${summary.failed || 0} failed, ${summary.skipped || 0} skipped`);
      }
      setShowPhotoWhatsAppPrompt(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send photo update');
    } finally {
      setSendingPhotoMessage(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <StickyActionHeader>
          <div>
            <div className="flex items-center gap-1">
              <h1 className="font-serif text-2xl font-bold text-wedding-charcoal" data-testid="photos-title">Photos</h1>
              <InfoTip
                content={canManageGuestExperience && workspaceTab === 'hero'
                  ? 'Manage the portrait and slideshow shown at the top of the guest view.'
                  : canManageGuestExperience && workspaceTab === 'photo-wall'
                    ? 'Manage guest uploads, QR locations, and the venue display.'
                    : 'Upload, organize, and share event photos.'}
                label="About photo management"
              />
            </div>
            {(!canManageGuestExperience || workspaceTab === 'album') && (
              <p className="text-muted-foreground">{photos.length} photos uploaded</p>
            )}
          </div>
          {(!canManageGuestExperience || workspaceTab === 'album') && (
            <div className="flex items-center gap-4 flex-wrap" data-header-action-group>
              <div className="flex items-center gap-2" data-header-secondary>
                <Switch
                  id="share"
                  checked={shareWithFeatured}
                  onCheckedChange={setShareWithFeatured}
                  data-testid="share-toggle"
                />
                <Label htmlFor="share" className="text-sm">Share with people in photo</Label>
              </div>
              <Button
                 className="bg-wedding-sage hover:bg-wedding-sage/90"
                 disabled={uploading}
                 onClick={() => document.getElementById('photo-upload').click()}
                 aria-label="Upload photos"
                 title="Upload photos"
                 data-header-action
                 data-testid="upload-photo-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload Photos'}
              </Button>
              <input
                id="photo-upload"
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={handleUpload}
              />
            </div>
          )}
        </StickyActionHeader>

        {canManageGuestExperience && (
          <Tabs
            value={workspaceTab}
            onValueChange={(value) => {
              setWorkspaceTab(value);
              window.sessionStorage.setItem('photo-management-workspace-tab', value);
            }}
          >
            <TabsList className="grid h-auto w-full max-w-3xl grid-cols-3 rounded-xl bg-wedding-sage/10 p-1">
              <TabsTrigger
                value="hero"
                className="gap-2 rounded-lg px-2 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="guest-view-hero-workspace-tab"
              >
                <VideoCamera className="h-5 w-5" />
                <span className="hidden sm:inline">Guest View Hero</span>
                <span className="sm:hidden">Hero</span>
              </TabsTrigger>
              <TabsTrigger
                value="album"
                className="gap-2 rounded-lg px-2 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="photo-album-workspace-tab"
              >
                <Images className="h-5 w-5" />
                Photo Album
              </TabsTrigger>
              <TabsTrigger
                value="photo-wall"
                className="gap-2 rounded-lg px-2 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="photo-wall-workspace-tab"
              >
                <Camera className="h-5 w-5" />
                <span className="hidden sm:inline">Live Photo Wall</span>
                <span className="sm:hidden">Photo Wall</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {canManageGuestExperience && workspaceTab === 'album' && (
          <EventScheduledReminderCard
            reminderKey="candid_photos"
            title="Photo reminders"
            description="Schedule WhatsApp reminders after the event so guests upload and view photos."
            compact
          />
        )}

        {canManageGuestExperience && workspaceTab === 'hero' && (
          <GuestExperienceMediaManager
            event={currentEvent}
            onRefresh={() => switchEvent(currentEvent.id)}
            galleryPhotos={photos}
            allowGallerySelection={user?.role === 'event_manager'}
            section="hero"
          />
        )}

        <div className={canManageGuestExperience && workspaceTab !== 'album' ? 'hidden' : 'space-y-6'}>
        {/* Face Recognition Card */}
        <Card className="bg-gradient-to-r from-wedding-sage/10 to-wedding-gold/10">
          <CardContent className="pt-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${hasFaceRegistered ? 'bg-green-100' : 'bg-gray-100'}`}>
                  {hasFaceRegistered ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : (
                    <UserCircle className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <h3 className="font-medium">Face Recognition</h3>
                  <InfoTip
                    content={hasFaceRegistered
                      ? 'Your face is registered. Photos featuring you will appear in My Photos.'
                      : 'Register your face to automatically find photos of yourself.'}
                    label="About face recognition"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                {hasFaceRegistered ? (
                  <>
                    <AIPremiumButton
                      featureName="AI face recognition"
                      variant="outline"
                      data-testid="scan-photos-btn"
                    >
                      {scanStatus?.has_new_photos ? 'Scan New Photos' : 'Scan Photos'}
                    </AIPremiumButton>
                    <Button 
                      variant="ghost"
                      onClick={removeFaceRegistration}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  </>
                ) : (
                  <AIPremiumButton
                    featureName="AI face recognition"
                    className="bg-wedding-sage hover:bg-wedding-sage/90"
                    data-testid="register-face-btn"
                  >
                    Register My Face
                  </AIPremiumButton>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        {user?.role === 'event_manager' && (
          <Card>
            <CardContent className="py-4 space-y-3">
              <div className="flex items-center gap-1">
                <p className="text-sm font-medium">{eventTypeConfig.labels.photoGalleryTitle} Link</p>
                <InfoTip
                  content="Add the photographer, media team, or shared-drive link so guests can view all photos directly. Save an empty field to remove the link."
                  label={`About ${eventTypeConfig.labels.photoGalleryTitle} link`}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="url"
                  placeholder="https://..."
                  value={galleryLinkInput}
                  onChange={(e) => setGalleryLinkInput(e.target.value)}
                  data-testid="gallery-link-input"
                />
                <Button
                  onClick={saveGalleryLink}
                  disabled={savingGalleryLink || !currentEvent?.id}
                  className="bg-wedding-sage hover:bg-wedding-sage/90"
                  data-testid="save-gallery-link-btn"
                >
                  {savingGalleryLink ? 'Saving...' : 'Save Link'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {currentEvent?.wedding_photos_link && (
          <Card className="mb-6 border-wedding-gold/40 bg-gradient-to-r from-wedding-gold/20 via-wedding-sage/10 to-white shadow-md">
            <CardContent className="py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <span className="inline-flex items-center rounded-full bg-wedding-gold/30 px-2.5 py-1 text-[0.6875rem] font-semibold tracking-wide text-wedding-charcoal">
                  Featured
                </span>
                <p className="mt-2 text-base font-semibold text-wedding-charcoal">{eventTypeConfig.labels.photoGalleryTitle}</p>
                <p className="text-sm text-muted-foreground">
                  {eventTypeConfig.labels.photoGalleryDescription}
                </p>
              </div>
              <a
                href={currentEvent.wedding_photos_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-wedding-sage px-4 py-2 text-sm font-medium text-white shadow hover:bg-wedding-sage/90 transition-colors"
              >
                Open Gallery <Camera className="w-4 h-4" />
              </a>
            </CardContent>
          </Card>
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 h-auto gap-1">
            <TabsTrigger value="my" data-testid="my-photos-tab" className="h-auto min-w-0 whitespace-normal flex flex-col items-start px-2 py-2 text-left leading-tight">
              <span className="flex flex-wrap items-center min-w-0 text-xs sm:text-sm">
                <User className="w-4 h-4 mr-2" /> My Photos
                <Badge variant="secondary" className="ml-1 sm:ml-2">{myPhotos.length}</Badge>
              </span>
              <span className="hidden sm:block text-xs text-muted-foreground">
                Photos that feature you.
              </span>
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="all-photos-tab" className="h-auto min-w-0 whitespace-normal flex flex-col items-start px-2 py-2 text-left leading-tight">
              <span className="flex flex-wrap items-center min-w-0 text-xs sm:text-sm">
                <Images className="w-4 h-4 mr-2" /> All Photos
                <Badge variant="secondary" className="ml-1 sm:ml-2">{photos.length}</Badge>
              </span>
              <span className="hidden sm:block text-xs text-muted-foreground">
                Everything shared for this event.
              </span>
            </TabsTrigger>
            <TabsTrigger value="wedding" data-testid="wedding-photos-tab" className="h-auto min-w-0 whitespace-normal flex flex-col items-start px-2 py-2 text-left leading-tight">
              <span className="flex flex-wrap items-center min-w-0 text-xs sm:text-sm">
                <Camera className="w-4 h-4 mr-2" /> {eventTypeConfig.labels.photoAlbumTab}
                <Badge variant="secondary" className="ml-1 sm:ml-2">{weddingAlbumPhotos.length}</Badge>
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            <PhotoGrid 
              photoList={photos} 
              emptyMessage="No photos uploaded yet. Be the first to share!" 
              loading={loading}
              onOpenLightbox={openLightbox}
              onDownload={handleDownload}
            />
          </TabsContent>

          <TabsContent value="my" className="mt-6">
            {!hasFaceRegistered && (
              <div className="mb-4 p-4 bg-wedding-sage/10 rounded-lg flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Register your face to see photos featuring you here automatically.
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setFaceDialogOpen(true)}
                  disabled={!modelsLoaded}
                >
                  Register Face
                </Button>
              </div>
            )}
            {hasFaceRegistered && scanStatus?.has_new_photos && (
              <div className="mb-4 p-4 bg-wedding-gold/10 rounded-lg flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {scanStatus.new_photo_count} new photo(s) were uploaded since your last scan. Scan again to update My Photos.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={scanPhotosForFaces}
                  disabled={scanningPhotos || !modelsLoaded}
                  data-testid="scan-new-photos-inline-btn"
                >
                  {scanningPhotos ? 'Scanning...' : 'Scan Now'}
                </Button>
              </div>
            )}
            <PhotoGrid 
              photoList={myPhotos} 
              emptyMessage="No photos featuring you yet." 
              loading={loading}
              onOpenLightbox={openLightbox}
              onDownload={handleDownload}
            />
          </TabsContent>

          <TabsContent value="wedding" className="mt-6">
            {weddingAlbumPhotos.length > 0 ? (
              <PhotoGrid 
                photoList={weddingAlbumPhotos} 
                emptyMessage="" 
                loading={loading}
                onOpenLightbox={openLightbox}
                onDownload={handleDownload}
              />
            ) : currentEvent?.wedding_photos_preview?.length > 0 ? (
              <div className="space-y-4">
                <PhotoGrid 
                  photoList={currentEvent.wedding_photos_preview.map((url, i) => ({ id: `preview-${i}`, url }))} 
                  emptyMessage="" 
                  loading={loading}
                  onOpenLightbox={openLightbox}
                  onDownload={handleDownload}
                />
                {currentEvent.wedding_photos_link && (
                  <div className="text-center">
                    <a 
                      href={currentEvent.wedding_photos_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-wedding-sage hover:underline font-medium"
                    >
                      View Full Album <Camera className="w-4 h-4" />
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Camera className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {eventTypeConfig.labels.photoAlbumEmpty}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        </div>

        {canManageGuestExperience && workspaceTab === 'photo-wall' && (
          <GuestExperienceMediaManager
            event={currentEvent}
            onRefresh={() => switchEvent(currentEvent.id)}
            section="photo-wall"
          />
        )}
      </div>

      <Dialog open={showPhotoWhatsAppPrompt} onOpenChange={setShowPhotoWhatsAppPrompt}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send photo update?</DialogTitle>
            <DialogDescription>
              Notify all guests on WhatsApp that the event photos are available.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPhotoWhatsAppPrompt(false)}
              disabled={sendingPhotoMessage}
            >
              Not Now
            </Button>
            <Button
              type="button"
              onClick={sendPhotoUpdateMessage}
              disabled={sendingPhotoMessage}
              className="bg-wedding-sage hover:bg-wedding-sage/90"
              data-testid="send-photo-message-btn"
            >
              {sendingPhotoMessage ? (
                <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <WhatsappLogo className="w-4 h-4 mr-2" />
              )}
              {sendingPhotoMessage ? 'Sending...' : 'Send to All Guests'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox Dialog */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-5xl h-[90vh] p-0 bg-black/95">
          <DialogHeader className="sr-only">
            <DialogTitle>Photo Viewer</DialogTitle>
          </DialogHeader>
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-white hover:bg-white/20 z-10"
              onClick={closeLightbox}
            >
              <X className="w-6 h-6" />
            </Button>
            
            {/* Navigation buttons */}
            {currentPhotoList.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 text-white hover:bg-white/20 z-10"
                  onClick={prevPhoto}
                >
                  <ArrowLeft className="w-6 h-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 text-white hover:bg-white/20 z-10"
                  onClick={nextPhoto}
                >
                  <ArrowRight className="w-6 h-6" />
                </Button>
              </>
            )}
            
            {/* Image */}
            {currentPhotoList[currentPhotoIndex] && (
              <img
                src={resolveMediaUrl(currentPhotoList[currentPhotoIndex].url, { preferProxy: true })}
                alt="Full size"
                className="max-w-full max-h-[80vh] object-contain"
              />
            )}
            
            {/* Bottom bar */}
            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
              <span className="text-white/70 text-sm">
                {currentPhotoIndex + 1} / {currentPhotoList.length}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleDownload(currentPhotoList[currentPhotoIndex])}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Face Registration Dialog */}
      <Dialog open={faceDialogOpen} onOpenChange={(open) => { if (!open) stopCamera(); setFaceDialogOpen(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Register Your Face
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Take a photo or upload one to enable automatic face recognition in event photos.
            </p>
            
            {cameraMode ? (
              <div className="space-y-4">
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={capturePhoto}
                    className="flex-1 bg-wedding-sage hover:bg-wedding-sage/90"
                    disabled={faceLoading}
                  >
                    {faceLoading ? (
                      <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4 mr-2" />
                    )}
                    Capture
                  </Button>
                  <Button variant="outline" onClick={stopCamera}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <Button
                  variant="outline"
                  className="h-24 border-dashed"
                  onClick={startCamera}
                  disabled={faceLoading}
                >
                  <div className="flex flex-col items-center gap-2">
                    <VideoCamera className="w-8 h-8 text-muted-foreground" />
                    <span>Use Camera</span>
                  </div>
                </Button>
                
                <div className="relative">
                  <Button
                    variant="outline"
                    className="w-full h-24 border-dashed"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={faceLoading}
                  >
                    <div className="flex flex-col items-center gap-2">
                      {faceLoading ? (
                        <SpinnerGap className="w-8 h-8 animate-spin" />
                      ) : (
                        <Upload className="w-8 h-8 text-muted-foreground" />
                      )}
                      <span>{faceLoading ? 'Processing...' : 'Upload Photo'}</span>
                    </div>
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>
              </div>
            )}
            
            <p className="text-xs text-muted-foreground text-center">
              For best results, use a clear front-facing photo with good lighting.
            </p>
          </div>
          
          <canvas ref={canvasRef} className="hidden" />
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
